/**
 * HustleMeetLobbyPage — Sprint 1 minimal lobby.
 *
 * URL: /meet
 *
 * Lets a signed-in user type a room name (or use a random suggestion)
 * and jump straight into the LiveKit-powered video room at
 * /meet/:roomName. Deliberately spartan for Sprint 1 — the scheduling
 * UI, calendar, and pre-join camera check come in Sprint 2.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Video, ArrowRight, Shuffle } from "lucide-react";

function randomRoom() {
  const words = [
    "aurora", "boomerang", "coral", "dingo", "eucalypt", "flint",
    "gecko", "harbour", "iron", "jarrah", "kelpie", "lagoon",
    "moray", "nectar", "opal", "possum", "quandong", "reef",
    "sandstone", "tinder", "uluru", "vagabond", "wattle", "yarra",
  ];
  const pick = () => words[Math.floor(Math.random() * words.length)];
  return `${pick()}-${pick()}-${Math.floor(Math.random() * 1000)}`;
}

export default function HustleMeetLobbyPage() {
  const [room, setRoom] = useState("");
  const navigate = useNavigate();

  const go = () => {
    const slug = (room || randomRoom())
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (slug) navigate(`/meet/${slug}`);
  };

  return (
    <div
      data-testid="hustlemeet-lobby"
      className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-emerald-950 text-white flex items-center justify-center px-6"
    >
      <div className="max-w-lg w-full">
        <div className="flex items-center gap-3 mb-6">
          <Video className="w-10 h-10 text-emerald-400" />
          <h1 className="text-3xl font-bold tracking-tight">HustleMeet</h1>
        </div>
        <p className="text-stone-400 mb-8 text-[15px] leading-relaxed">
          Video meetings for your team, powered by LiveKit. Enter a room
          name below — anyone with a HustleSuite account
          can join by visiting the same link.
        </p>
        <label className="text-[11px] uppercase tracking-widest font-bold text-stone-400 block mb-2">
          Room name
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            data-testid="hustlemeet-room-input"
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && go()}
            placeholder="e.g. weekly-standup"
            autoFocus
            className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-stone-500 focus:outline-none focus:border-emerald-400"
          />
          <button
            type="button"
            data-testid="hustlemeet-random-btn"
            onClick={() => setRoom(randomRoom())}
            className="px-3 py-3 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20"
            title="Suggest a room name"
          >
            <Shuffle className="w-4 h-4" />
          </button>
        </div>
        <button
          type="button"
          data-testid="hustlemeet-join-btn"
          onClick={go}
          className="w-full mt-4 px-5 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-stone-950 font-semibold flex items-center justify-center gap-2 transition-colors"
        >
          Join meeting <ArrowRight className="w-4 h-4" />
        </button>
        <p className="mt-6 text-[11px] text-stone-500 leading-relaxed">
          Share this link with your team:
          <br />
          <code className="text-emerald-400/80 break-all">
            {window.location.origin}/meet/{room || "your-room-name"}
          </code>
        </p>
      </div>
    </div>
  );
}
